import React, { useState } from 'react';
import { FileTextIcon } from './icons/FileTextIcon';
import { showFeedbackDialog } from '../lib/sentry';
import type { FileProcessingStatus } from './BatchProcessingView';
import { exportNotebookPack } from '../utils/exportUtils';
import { RedactSession } from '../utils/registryUtils';
import PostRedactionSaveModal from './PostRedactionSaveModal';

interface BatchResultDisplayProps {
  results: FileProcessingStatus[];
  onReset: () => void;
  showWatermark?: boolean;
  redactSession: RedactSession;
  onOpenSettings?: () => void;
}

const BatchResultDisplay: React.FC<BatchResultDisplayProps> = ({ 
  results, 
  onReset,
  showWatermark = false,
  redactSession,
  onOpenSettings,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [saveModalTarget, setSaveModalTarget] = useState<FileProcessingStatus | null>(null);
  const batchFiles = results;

  const successResults = results.filter(r => r.status === 'success');
  const errorResults = results.filter(r => r.status === 'error');

  const getExportPlainTextForResult = (result: FileProcessingStatus): string => {
    if (!result.result) return '';
    return showWatermark
      ? `${result.result}\n\n---\nRedacted with Redact AI (Free Tier)`
      : result.result;
  };

  const handleCopy = async (text: string, index: number) => {
    try {
      const textToCopy = showWatermark ? `${text}\n\n---\nRedacted with Redact AI (Free Tier)` : text;
      await navigator.clipboard.writeText(textToCopy);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenSaveModalForResult = (result: FileProcessingStatus) => {
    if (!result.result) return;
    setSaveModalTarget(result);
  };

  /** Bulk download skips the modal so users aren’t blocked by N dialogs; filenames stay generic */
  const handleDownloadAllNoModal = () => {
    successResults.forEach((result, index) => {
      setTimeout(() => {
        if (!result.result) return;
        const content = getExportPlainTextForResult(result);
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const baseName = result.file.name.replace(/\.[^/.]+$/, '');
        const rawName = result.autoName ?? `redacted_${baseName}.txt`;
        a.download = rawName.replace(/\.[^/.]+$/, '') + '.txt';

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, index * 200);
    });
  };

  const handleExportNotebookPack = async () => {
    await exportNotebookPack(batchFiles, redactSession, showWatermark);
  };

  const handleReportIssue = (result: FileProcessingStatus) => {
    if (result.sentryEventId) {
      showFeedbackDialog(result.sentryEventId);
    }
  };

  return (
    <div className="w-full p-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
          Batch Processing Complete
        </h2>
        <p className="text-slate-500 dark:text-slate-400">
          {successResults.length} of {results.length} files processed successfully
          {errorResults.length > 0 && (
            <span className="text-red-500"> ({errorResults.length} failed)</span>
          )}
        </p>
      </div>

      <div className="mb-6 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Human review is required before uploading to an AI tool or sharing. Automated preparation can miss direct or indirect identifiers, especially on OCR pages.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-3 mb-6">
          <button
            onClick={handleExportNotebookPack}
            disabled={successResults.length === 0}
            className="px-4 py-2 bg-slate-900 dark:bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
            </svg>
            Export Notebook Pack (.zip)
          </button>
          <button
            onClick={handleDownloadAllNoModal}
            disabled={successResults.length === 0}
            className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download All ({successResults.length})
          </button>
      </div>

      {/* Results Accordion */}
      <div className="space-y-3">
        {results.map((result, index) => (
          <div 
            key={index}
            className={`rounded-lg border overflow-hidden transition-all ${
              result.status === 'success'
                ? 'border-slate-200 dark:border-slate-700'
                : 'border-red-200 dark:border-red-700'
            }`}
          >
            {/* Header */}
            <button
              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                result.status === 'success'
                  ? 'bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800'
                  : 'bg-red-50 dark:bg-red-900/20'
              }`}
            >
              <div className="flex items-center gap-3">
                {result.status === 'success' ? (
                  <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <FileTextIcon className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-[250px]">
                    {result.file.name}
                  </span>
                </div>
              </div>
              <svg 
                className={`w-5 h-5 text-slate-400 transition-transform ${expandedIndex === index ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded Content */}
            {expandedIndex === index && (
              <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                {result.status === 'success' && result.result ? (
                  <>
                    {result.ocrPageFailures && result.ocrPageFailures.length > 0 && (
                      <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          {result.ocrPageFailures.length} of {result.pageCount ?? result.ocrPageFailures.length} pages could not be OCR&apos;d.
                          Review carefully — those pages are marked in the text.
                        </p>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 mb-3">
                      <button
                        onClick={() => handleCopy(result.result!, index)}
                        className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-1"
                      >
                        {copiedIndex === index ? (
                          <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleOpenSaveModalForResult(result)}
                        className="px-3 py-1.5 text-sm bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-md hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Save / Download
                      </button>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300 font-mono">
                        {result.result}
                      </pre>
                      {showWatermark && (
                        <p className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-400 dark:text-slate-500 italic">
                          Redacted with Redact AI (Free Tier)
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div>
                    <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                      {result.error || 'An error occurred while processing this file.'}
                    </p>
                    {result.sentryEventId && (
                      <button
                        onClick={() => handleReportIssue(result)}
                        className="text-sm text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 underline flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Report this issue
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer Actions */}
      <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
        <button
          onClick={onReset}
          className="w-full py-3 px-6 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
        >
          Process More Files
        </button>
      </div>

      {showWatermark && (
        <p className="mt-4 text-center text-xs text-slate-400 dark:text-slate-500">
          Upgrade to Pro to remove watermarks and unlock batch processing for up to 5 files.
        </p>
      )}

      {saveModalTarget?.result ? (
        <PostRedactionSaveModal
          isOpen
          sourceFileName={saveModalTarget.file.name}
          redactedText={getExportPlainTextForResult(saveModalTarget)}
          outputExtension="txt"
          onClose={() => setSaveModalTarget(null)}
          onOpenSettings={onOpenSettings}
        />
      ) : null}
    </div>
  );
};

export default BatchResultDisplay;
